/**
 * Outcome dialog (Specs/win-loss-harvest-spec.md §2.2, widened in R4 plan §2.7)
 *
 * One of five outcomes. Score, rank, bidders, reason, feedback and the debrief
 * letter describe a result, so they show only for Won or Lost. Saving sends
 * `_outcome` alone, which records the result and closes the bid in one step.
 * Edit result opens pre-filled from the saved outcome. The debrief letter
 * uploads as a bid-scoped document (evaluator_debrief): indexed for tender
 * chat, never placed in the org library.
 */

import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Paperclip, Loader2, Library } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useHarvestStore } from '@/stores/harvest-store'
import {
  api,
  toastApiError,
  requestUploadUrl,
  uploadFileToS3,
  type PrimaryOutcomeReason,
} from '@/lib/api'
import { cn } from '@/lib/utils'
import { useBidsStore } from '@/stores/bids-store'
import {
  BID_OUTCOMES,
  OUTCOME_LABELS,
  bidLifecycleOf,
  outcomeHasResult,
  type BidOutcomeValue,
} from '@/lib/bid-lifecycle'

const REASONS: Array<{ value: PrimaryOutcomeReason; label: string }> = [
  { value: 'price_value', label: 'Price / Value' },
  { value: 'methodology', label: 'Methodology' },
  { value: 'experience', label: 'Experience' },
  { value: 'personnel', label: 'Key Personnel' },
  { value: 'incumbency', label: 'Incumbent / Relationship' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'social_local', label: 'Local & Social Outcomes' },
  { value: 'other', label: 'Other' },
]

export function OutcomeDialog() {
  const dialog = useHarvestStore((s) => s.outcomeDialog)
  const harvestStatus = useHarvestStore((s) => s.status)
  const statusBidId = useHarvestStore((s) => s.statusBidId)
  // Only this bid's harvest status counts: the store may still hold another's.
  const status = dialog && statusBidId === dialog.bidId ? harvestStatus : null
  const bidOutcome = useBidsStore((s) => {
    if (!dialog) return null
    const bid = s.selectedBid?.id === dialog.bidId ? s.selectedBid : s.bids.find((b) => b.id === dialog.bidId)
    return bid ? bidLifecycleOf(bid).outcome : null
  })
  // "Saving closes the bid" is only true while the bid is still open.
  const isBidClosed = useBidsStore((s) => {
    if (!dialog) return false
    const bid = s.selectedBid?.id === dialog.bidId ? s.selectedBid : s.bids.find((b) => b.id === dialog.bidId)
    return bid ? bidLifecycleOf(bid).lifecycle === 'closed' : false
  })
  const isSaving = useHarvestStore((s) => s.isSaving)
  const saveOutcome = useHarvestStore((s) => s.saveOutcome)
  const closeOutcomeDialog = useHarvestStore((s) => s.closeOutcomeDialog)

  const [outcome, setOutcome] = useState<BidOutcomeValue | null>(null)
  const [score, setScore] = useState('')
  const [rank, setRank] = useState('')
  const [totalBidders, setTotalBidders] = useState('')
  const [reason, setReason] = useState<PrimaryOutcomeReason | null>(null)
  const [feedback, setFeedback] = useState('')
  const [debriefDocumentId, setDebriefDocumentId] = useState<string | null>(null)
  const [debriefName, setDebriefName] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  // Reset per dialog open, then prefill once from the saved outcome row (Edit
  // result) when it arrives, unless the user has already started choosing.
  const touched = useRef(false)
  const openKey = dialog ? `${dialog.bidId}:${dialog.outcome ?? ''}` : null
  useEffect(() => {
    touched.current = false
  }, [openKey])

  useEffect(() => {
    if (!dialog || touched.current) return
    const existing = status?.outcome
    setOutcome(dialog.outcome ?? existing?.outcome ?? bidOutcome ?? null)
    setScore(existing?.score != null ? String(existing.score) : '')
    setRank(existing?.rank != null ? String(existing.rank) : '')
    setTotalBidders(existing?.totalBidders != null ? String(existing.totalBidders) : '')
    setReason(existing?.primaryReason ?? null)
    setFeedback(existing?.feedbackText ?? '')
    setDebriefDocumentId(existing?.debriefDocumentId ?? null)
    setDebriefName(existing?.debriefDocumentId ? 'Debrief letter on file' : null)
  }, [dialog, status, bidOutcome])

  if (!dialog) return null

  const harvestedCount =
    status?.artifacts.filter((a) => a.harvestedDocumentId).length ?? 0

  const handleDebriefUpload = async (file: File) => {
    setUploading(true)
    try {
      const { documentId, uploadUrl, s3Key } = await requestUploadUrl(
        file.name,
        file.type,
        file.size,
        dialog.bidId
      )
      await uploadFileToS3(uploadUrl, file)
      await api.post('/documents', {
        documentId,
        name: file.name,
        type: 'evaluator_debrief',
        mimeType: file.type,
        size: file.size,
        s3Key,
        bidId: dialog.bidId,
      })
      setDebriefDocumentId(documentId)
      setDebriefName(file.name)
    } finally {
      setUploading(false)
    }
  }

  const toNumber = (value: string): number | null => {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : null
  }

  const showResultFields = outcomeHasResult(outcome)
  const isEditing = status?.outcome != null || bidOutcome != null

  const handleSave = async () => {
    if (!outcome) return
    // Result fields only for won or lost: the server refuses them otherwise.
    const input = showResultFields
      ? {
          outcome,
          score: toNumber(score),
          rank: toNumber(rank),
          totalBidders: toNumber(totalBidders),
          primaryReason: reason,
          feedbackText: feedback.trim() || null,
          debriefDocumentId,
        }
      : { outcome }
    try {
      await saveOutcome(input)
      toast.success(`Result saved: ${OUTCOME_LABELS[outcome]}`)
    } catch (error) {
      toastApiError(error, 'Could not save the result')
    }
  }

  const touch = () => {
    touched.current = true
  }

  return (
    <Dialog open onOpenChange={(open) => !open && closeOutcomeDialog()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit the result' : 'Record the result'}</DialogTitle>
          <DialogDescription>
            {dialog.bidTitle}.{isBidClosed ? '' : ' Saving closes the bid.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-meta" id="outcome-choice-label">Result</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5" role="radiogroup" aria-labelledby="outcome-choice-label">
              {BID_OUTCOMES.map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={outcome === value}
                  onClick={() => {
                    touch()
                    setOutcome(value)
                  }}
                  className={cn(
                    'rounded-lg border px-2.5 py-1 text-xs transition-colors',
                    outcome === value
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border hover:bg-accent'
                  )}
                >
                  {OUTCOME_LABELS[value]}
                </button>
              ))}
            </div>
          </div>

          {showResultFields && (
          <>
          <p className="text-meta">Everything below is optional, but evaluator feedback compounds.</p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-meta" htmlFor="outcome-score">Score</label>
              <Input
                id="outcome-score"
                value={score}
                onChange={(e) => { touch(); setScore(e.target.value) }}
                placeholder="e.g. 82.5"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <label className="text-meta" htmlFor="outcome-rank">Rank</label>
              <Input
                id="outcome-rank"
                value={rank}
                onChange={(e) => { touch(); setRank(e.target.value) }}
                placeholder="e.g. 2"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <label className="text-meta" htmlFor="outcome-bidders">Bidders</label>
              <Input
                id="outcome-bidders"
                value={totalBidders}
                onChange={(e) => { touch(); setTotalBidders(e.target.value) }}
                placeholder="e.g. 5"
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div>
            <p className="text-meta">
              {outcome === 'won' ? 'Why we won' : 'Primary reason'}
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {REASONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    touch()
                    setReason(reason === option.value ? null : option.value)
                  }}
                  className={cn(
                    'rounded-lg border px-2 py-1 text-xs transition-colors',
                    reason === option.value
                      ? 'border-foreground bg-foreground text-background'
                      : 'border-border hover:bg-accent'
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-meta" htmlFor="outcome-feedback">
              Evaluator feedback
            </label>
            <Textarea
              id="outcome-feedback"
              value={feedback}
              onChange={(e) => { touch(); setFeedback(e.target.value) }}
              placeholder="Paste the debrief email or evaluator comments…"
              className="min-h-[80px] text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="debrief-file"
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  touch()
                  void handleDebriefUpload(file)
                }
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              disabled={uploading}
              onClick={() => document.getElementById('debrief-file')?.click()}
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
              {debriefName ? 'Replace debrief letter' : 'Attach debrief letter'}
            </Button>
            {debriefName && (
              <span className="min-w-0 truncate text-meta">{debriefName}</span>
            )}
          </div>

          {harvestedCount > 0 && (
            <p className="flex items-center gap-1.5 text-meta">
              <Library className="h-3.5 w-3.5" />
              {harvestedCount} document{harvestedCount === 1 ? '' : 's'} from this bid{' '}
              {harvestedCount === 1 ? 'is' : 'are'} in your library. This result will be recorded on{' '}
              {harvestedCount === 1 ? 'it' : 'them'}.
            </p>
          )}
          </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" disabled={isSaving} onClick={closeOutcomeDialog}>
            Not now
          </Button>
          <Button disabled={isSaving || uploading || !outcome} onClick={() => void handleSave()}>
            {isSaving ? 'Saving…' : 'Save result'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
